import clonedeep from 'lodash.clonedeep';
import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { PageId, WidgetId } from 'types/idTypes';
import { InfographState, WidgetsMap } from 'types/infographTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { findAllPropertyValues } from 'utils/findAllPropertyValues';
import { roundToDecimals } from 'utils/number';
import { setAllMatchingKeys } from 'utils/setAllPropertyValues';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { getTextWidgetProperties, setFontFamily } from 'widgets/TextBasedWidgets/TextWidget/TextWidgetData.helpers';
import { getRawText } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.helpers';
import { TextBasedWidgetData, TextWidgetTag } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { findClosestWidgetBelow, getPageWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';

import {
  BELOW_WIDGET_DISTANCE,
  EXPLORE_FONTS_CONFIG,
  FontSizeTreshold,
  HEADING_TRESHOLD,
  TextCharCountThreshold,
  Weight,
} from './ApplyFonts.config';
import { ApplyFont, Label, LabelTextOptions, LabeledTextWidgets } from './ApplyFonts.types';

/**
 * Returns an array of fonts that are displayed in explore fonts (so we can load the fonts)
 * (its ok if the array is not unique)
 *
 * @returns {string[]} - Array of fonts
 */
export const getInitalExploreFonts = (): string[] => {
  const initialFonts: string[] = [];

  for (let i = 0; i < EXPLORE_FONTS_CONFIG.length; i++) {
    initialFonts.push(EXPLORE_FONTS_CONFIG[i].title);
    initialFonts.push(EXPLORE_FONTS_CONFIG[i].heading);
    initialFonts.push(EXPLORE_FONTS_CONFIG[i].paragraph);
  }

  return initialFonts;
};

// remove underscores from the custom font name
const UNDERSOCRE_REGEX = /_/g;

/**
 * Formats the uploaded font name by replacing underscores with spaces.
 *
 * @param fontName - The font name to be formatted.
 * @returns The formatted font name.
 */
export const formatUploadedFontName = (fontName: string | undefined) => {
  if (!fontName) return '';
  return fontName.replace(UNDERSOCRE_REGEX, ' ');
};

const NUMBER_REGEX = /\d/;

/**
 * Determines whether the given text is considered decorative.
 * Infographs often have large numbers or single letters text widgets that are used for decorative purposes.
 *
 * @param proseMirrorData - The ProseMirror data object.
 * @param widgetIds - An array of widget IDs.
 * @returns A boolean indicating whether the text is decorative.
 */
const isDecorativeText = (proseMirrorData: object, widgetIds: WidgetId[]): boolean => {
  const rawText = getRawText(proseMirrorData, 100, false);
  const isCountCharacter = rawText.length === 1 || rawText.length === 2;
  if (isCountCharacter) return true;

  const hasNumber = rawText.length === 3 && NUMBER_REGEX.test(rawText);
  if (hasNumber) return true;

  const isYearAndMultiple = rawText.length === 4 && NUMBER_REGEX.test(rawText) && widgetIds.length > 1;
  if (isYearAndMultiple) return true;

  return false;
};

/**
 * Labels the text as either title, heading or paragraph
 * based on the options and the weights
 * @param options - The options to label the text
 * @returns The label
 */
const labelText = (options: LabelTextOptions): Label => {
  const { textTag, pageFontSizeCount, fontSize, isLargestPageFontSize, text, isFirstPage, prevLabel } = options;

  const textLength = text.length;
  const isShortTextLength = textLength <= TextCharCountThreshold.Short;
  const isLongParagraph = textLength >= TextCharCountThreshold.Long;
  const endsWithAPeriod = text.endsWith('.');
  const isPTag = textTag === TextWidgetTag.Paragraph;
  const isHeadingTag = !isPTag;

  const isLargeFontSize = fontSize >= FontSizeTreshold.Large;
  const isSmallFontSize = fontSize <= FontSizeTreshold.Small;
  const isTinyFontSize = fontSize <= FontSizeTreshold.Tiny;
  const hasMultipleLines = text.split('\n').length > 3;

  const hasBeenLabeledAsHeading = prevLabel === 'heading';
  const hasBeenLabeledAsParagraph = prevLabel === 'paragraph';
  const hasBeenLabeledAsTitlte = prevLabel === 'title';

  // set the score based on the options and the weights
  let score = 0;
  if (isShortTextLength) score += Weight.ShortText;
  if (isLargeFontSize) score += Weight.LargeFontSize;
  if (isSmallFontSize) score += Weight.SmallFontSize;
  if (isTinyFontSize) score += Weight.TinyFontSize;
  if (endsWithAPeriod) score += Weight.EndsInPeriod;
  if (options.isEntireTextBold) score += Weight.Bold;
  if (options.isAboveLongParagraph) score += Weight.isAboveLongParagraph;
  if (options.hasList) score += Weight.List;
  if (options.isAboveText) score += Weight.AboveText;
  if (isHeadingTag && options.isTaggedDocument) score += Weight.H2H6Tag;
  if (isPTag && options.isTaggedDocument) score += Weight.PTag;
  if (hasBeenLabeledAsHeading) score += Weight.HasBeenClassifiedAsHeading;
  if (hasBeenLabeledAsParagraph) score += Weight.HasBeenClassifiedAsParagraph;
  if (hasMultipleLines) score += Weight.LineCount;
  if (isLongParagraph) score += Weight.LongParagraph;

  // the score is above the treshold - we're going to label it as a heading
  if (score > HEADING_TRESHOLD) {
    // check if it should be the title or heading
    if (hasBeenLabeledAsTitlte) return 'title';
    if (options.isTheLargestFontSize && isFirstPage) return 'title';
    if (isLargestPageFontSize && pageFontSizeCount[fontSize] === 1 && !hasBeenLabeledAsHeading) {
      return 'title';
    }

    return 'heading';
  }

  // else we're going to label it as a paragraph
  return 'paragraph';
};

/**
 * Labels all text widgets in the infograph as either title, heading or paragraph
 *
 * @param infograph
 * @returns
 */
export const labelTextWidgets = (infograph: InfographState): LabeledTextWidgets => {
  const widgets = infograph.widgets;
  const pageOrder = infograph.pageOrder;
  const widgetIds: WidgetId[] = Object.keys(widgets);

  // text widgets for a specific font size - e.g. { 14: ['widgetId1', 'widgetId2'] }
  const fontSizeMap: { [fontSize: number]: WidgetId[] } = {};
  // text widgets and their font size - e.g. { 'widgetId1': 14 }
  const widgetIdFontSizeMap: { [widgetId: WidgetId]: number } = {};
  // all widget ids per page id - e.g. { 'pageId1': ['widgetId1', 'widgetId2'] }
  const pageIdMap: { [pageId: PageId]: WidgetId[] } = {};
  // widget id to page id - e.g. { 'widgetId1': 'pageId1' }
  const widgetPageIdMap: { [widgetId: WidgetId]: PageId } = {};
  // tag count - how many different tags are in the document
  const tagCountMap: { [textTag: string]: number } = {};
  // font map - e.g. { '14_Arial_false': 'title' } - store previously labeled fonts (per family, font size, bold )
  type LabelStyleMap = { [key: string]: Label };
  const labelStyleMap: LabelStyleMap = {};

  // this will be returned by the function
  const labeledTextWidgets: LabeledTextWidgets = [];

  // iterate over the widgets and categorize them by font size
  for (let i = 0; i < widgetIds.length; i++) {
    const widgetId = widgetIds[i];

    // text widget
    if (getWidgetTypeFromId(widgetId) === WidgetType.Text) {
      const widget = widgets[widgetId] as TextWidgetData;

      // if a text widget has multiple font sizes we're just going to use the first one
      const fontSizes = findAllPropertyValues(widget, ['fontSize']);
      const fontSize = roundToDecimals(parseFloat((fontSizes[0] as string) || '0px'), 2);
      if (!fontSize) continue;

      if (!fontSizeMap[fontSize]) fontSizeMap[fontSize] = [widgetId];
      else fontSizeMap[fontSize].push(widgetId);
      widgetIdFontSizeMap[widgetId] = fontSize;

      // check if there are different tags
      if (!tagCountMap[widget.textTag]) tagCountMap[widget.textTag] = 1;
    }
  }

  // iterate over the pages - and get the widgets per page
  for (let i = 0; i < pageOrder.length; i++) {
    const id = pageOrder[i];
    const page = infograph.pages[id];
    const widgetIdsOnPage = getPageWidgetIds(page, widgets);

    widgetIdsOnPage.forEach((widgetId: string) => {
      widgetPageIdMap[widgetId] = id;
    });

    // sort widgetIdsOnPage by fontSize
    const sortedWidgetIdsOnPage = widgetIdsOnPage.sort((a: WidgetId, b: WidgetId) => {
      // only sort by font size if the widget is a text widget
      const fontSizeA = getWidgetTypeFromId(a) === WidgetType.Text ? widgetIdFontSizeMap[a] : 0;
      const fontSizeB = getWidgetTypeFromId(b) === WidgetType.Text ? widgetIdFontSizeMap[b] : 0;

      return fontSizeB - fontSizeA;
    });

    pageIdMap[id] = sortedWidgetIdsOnPage;
  }

  // iterate over widgets page by page and label them
  for (let i = 0; i < pageOrder.length; i++) {
    const pageId = pageOrder[i];
    const isFirstPage = pageId === pageOrder[0];
    const widgetIdsOnPage = pageIdMap[pageId];

    // store all the font sizes on the page
    let pageFontSizes: number[] = [];
    // store the count of each font size on the page
    const pageFontSizeCount: { [fontSize: number]: number } = {};

    // The text and table widgets on the page
    // (we need the table here since a text above a table is more likely to be a heading)
    const pageWidgets: { [widgetId: WidgetId]: Widget & { widgetId: WidgetId } } = {};

    widgetIdsOnPage.forEach((id: WidgetId) => {
      const isText = getWidgetTypeFromId(id) === WidgetType.Text;
      const isTable = getWidgetTypeFromId(id) === WidgetType.Table;

      // table or text only
      if (isText || isTable) pageWidgets[id] = { ...widgets[id], widgetId: id };

      if (isText) {
        const fontSizeNr = widgetIdFontSizeMap[id];

        pageFontSizes.push(fontSizeNr);
        if (!pageFontSizeCount[fontSizeNr]) pageFontSizeCount[fontSizeNr] = 1;
        else pageFontSizeCount[fontSizeNr] += 1;
      }
    });

    // sort the font sizes in descending order and remove duplicates
    pageFontSizes = [...new Set(pageFontSizes)];
    pageFontSizes.sort((a: number, b: number) => b - a);

    // if there is more than one type of tag - the document is considered as tagged by the user
    const isTaggedDocument = Object.keys(tagCountMap).length > 1;

    // find the largest font size on the page - this will be used to determine the title
    let pageLargestFontSize: number | undefined;
    for (let f = 0; f < pageFontSizes.length; f++) {
      const fontSize = pageFontSizes[f];
      // look at all the widgets with this font size
      const widgetIds = fontSizeMap[fontSize];

      // iterate over the widgets with this font size
      for (let k = 0; k < widgetIds.length; k++) {
        const id = widgetIds[k];
        const isText = getWidgetTypeFromId(id) === WidgetType.Text;
        if (!isText) continue;

        // should be on the same page
        if (widgetPageIdMap[id] !== pageId) continue;

        // Decorative text should not be considered for titles (e.g. numbers, single letters, etc.)
        if (isDecorativeText((widgets[id] as TextWidgetData).proseMirrorData, widgetIds)) continue;

        // we found the largest font size - stop
        pageLargestFontSize = fontSize;

        break;
      }

      if (pageLargestFontSize) break;
    }

    for (let j = 0; j < widgetIdsOnPage.length; j++) {
      const widgetId = widgetIdsOnPage[j];
      const isText = getWidgetTypeFromId(widgetId) === WidgetType.Text;
      if (!isText) continue;

      const widget = widgets[widgetId] as TextWidgetData;
      const { textTag, proseMirrorData } = widget;
      const { text, fontSize, isEntireTextBold, hasList, fontFamily } = getTextWidgetProperties(proseMirrorData);

      const key = `${fontSize}_${isEntireTextBold}_${fontFamily}`;
      // we don't include bold in the title key title as its common with bold/nonbold same title
      const titleKey = `${fontSize}_${fontFamily}`;
      const prevLabel = labelStyleMap[titleKey] || labelStyleMap[key];

      const selectedWidget = { ...widget, widgetId: widgetId };

      // check the relation of the widget to nearby widgets
      const { isAboveLongParagraph, isAboveText } = checkNearbyWidgets(selectedWidget, pageWidgets, widgets);

      const isMultiPage = pageOrder.length > 1;
      const isLargestPageFontSize = fontSize === pageFontSizes[0] && isMultiPage;
      const isTheLargestFontSize = fontSize === pageLargestFontSize;

      const textType = labelText({
        isEntireTextBold,
        hasList,
        isAboveText,
        isAboveLongParagraph,
        isTheLargestFontSize,
        pageFontSizeCount,
        fontSize,
        isLargestPageFontSize,
        text,
        isTaggedDocument,
        isFirstPage,
        prevLabel,
        textTag,
      });

      labelStyleMap[textType === 'title' ? titleKey : key] = textType;
      labeledTextWidgets.push({ widgetId, textType });
    }
  }

  return labeledTextWidgets;
};

/**
 * Checks if the widget is above a text widget or a long paragraph
 *
 * @param selectedWidget - The selected widget
 * @param pageTextTableWidgets - The text and table widgets on the page
 * @param widgets - The widgetsMap
 *
 * @returns { isAboveText: boolean, isAboveLongParagraph: boolean }
 */
const checkNearbyWidgets = (
  selectedWidget: Widget & { widgetId: string },
  pageTextTableWidgets: WidgetsMap,
  widgets: WidgetsMap,
): { isAboveText: boolean; isAboveLongParagraph: boolean } => {
  const closestWidgetBelow = findClosestWidgetBelow(selectedWidget, pageTextTableWidgets, BELOW_WIDGET_DISTANCE);
  if (!closestWidgetBelow) return { isAboveText: false, isAboveLongParagraph: false };

  // find the closest widget below the current widget on the same page
  const isAboveText = !!closestWidgetBelow;
  const isBelowTable =
    closestWidgetBelow.widgetId && getWidgetTypeFromId(closestWidgetBelow.widgetId) === WidgetType.Table;

  // check if the widget below is a long paragraph
  let isAboveLongParagraph = false;
  if (isAboveText && !isBelowTable && closestWidgetBelow.widgetId) {
    // if the text below is long - we're adding more weight to the heading score here
    const closestBelowWidgetData = widgets[closestWidgetBelow.widgetId];
    const textWidgetBelowProperties = getTextWidgetProperties(
      (closestBelowWidgetData as TextBasedWidgetData).proseMirrorData,
    );
    if (textWidgetBelowProperties.text?.length > TextCharCountThreshold.Long) isAboveLongParagraph = true;
  }

  return { isAboveText, isAboveLongParagraph };
};

/**
 * Applies the font config to the labeled text widgets and other widgets with a fontfamily
 *
 * @param widgets - The widgets
 * @param labeledTextWidgets - The labeled text widgets
 * @param fontConfig - The font config with heading, title and paragraph
 * @returns The updated widgets
 */
export const applyFont = (
  widgets: WidgetsMap,
  labeledTextWidgets: LabeledTextWidgets,
  fontConfig: ApplyFont,
): UpdateWidget[] => {
  const updateWidgets: UpdateWidget[] = [];
  const widgetIds: WidgetId[] = Object.keys(widgets);

  // Apply the new fonts to the text widgets and fit the size to the bounding box
  for (let i = 0; i < labeledTextWidgets.length; i++) {
    const { widgetId, textType } = labeledTextWidgets[i];
    const isText = getWidgetTypeFromId(widgetId) === WidgetType.Text;

    if (isText) {
      const newFontFamily = fontConfig[textType];
      const fontFamily = findAllPropertyValues(widgets[widgetId], ['fontFamily']);

      // The text widget already has this font so we don't need to update it
      if (fontFamily.length === 1 && fontFamily[0] === newFontFamily) continue;

      const widgetData = clonedeep(widgets[widgetId]);
      const updatedWidgetData = setFontFamily(widgetData as TextWidgetData, newFontFamily);
      updateWidgets.push({ widgetId, widgetData: updatedWidgetData });
      continue;
    }
  }

  // Apply the new fonts to the non-text widgets (e.g. charts / statcharts / table)
  for (let i = 0; i < widgetIds.length; i++) {
    const widgetId = widgetIds[i];
    const isText = getWidgetTypeFromId(widgetId) === WidgetType.Text;
    if (isText) continue;

    // check if the widget has a font that can be updated
    const fontFamily = findAllPropertyValues(widgets[widgetId], ['fontFamily']);
    const hasFontFamily = fontFamily?.length > 0;
    if (!hasFontFamily) continue;

    // The widget already has the new font so we don't need to update it
    if (fontFamily.length === 1 && fontFamily[0] === fontConfig.paragraph) continue;

    const widgetData = clonedeep(widgets[widgetId]);
    setAllMatchingKeys(widgetData, 'fontFamily', fontConfig.paragraph);
    updateWidgets.push({ widgetId, widgetData: widgetData });
  }

  return updateWidgets;
};
